/**
 * Security utilities for input validation and sanitization
 */

// Email validation regex (RFC 5322 compliant)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Phone validation regex (international format)
const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

// Name validation (letters, spaces, hyphens, apostrophes only)
const NAME_REGEX = /^[a-zA-Z\s\-']+$/;

/**
 * Validates email address format
 */
export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  return EMAIL_REGEX.test(email.trim().toLowerCase());
};

/**
 * Validates phone number format
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false;
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  return PHONE_REGEX.test(cleaned);
};

/**
 * Validates name format (no special characters except hyphens and apostrophes)
 */
export const validateName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  return trimmed.length >= 2 && trimmed.length <= 50 && NAME_REGEX.test(trimmed);
};

/**
 * Sanitizes text input by removing HTML tags and limiting length
 */
export const sanitizeText = (text: string, maxLength: number = 1000): string => {
  if (!text || typeof text !== 'string') return '';
  
  // Remove HTML tags
  const withoutHtml = text.replace(/<[^>]*>/g, '');
  
  // Trim and limit length
  return withoutHtml.trim().substring(0, maxLength);
};

/**
 * Sanitizes phone number by removing non-numeric characters
 */
export const sanitizePhone = (phone: string): string => {
  if (!phone || typeof phone !== 'string') return '';
  return phone.replace(/[^0-9+]/g, '');
};

/**
 * Validates and sanitizes form data for property inquiries
 */
export const validateInquiryForm = (data: {
  client_name: string;
  client_email: string;
  client_phone?: string;
  message?: string;
}) => {
  const errors: string[] = [];
  const sanitized = { ...data };

  // Validate and sanitize name
  if (!validateName(data.client_name)) {
    errors.push('Please enter a valid name (2-50 characters, letters only)');
  } else {
    sanitized.client_name = sanitizeText(data.client_name, 50);
  }

  // Validate and sanitize email
  if (!validateEmail(data.client_email)) {
    errors.push('Please enter a valid email address');
  } else {
    sanitized.client_email = data.client_email.trim().toLowerCase();
  }

  // Validate and sanitize phone (optional)
  if (data.client_phone) {
    if (!validatePhone(data.client_phone)) {
      errors.push('Please enter a valid phone number');
    } else {
      sanitized.client_phone = sanitizePhone(data.client_phone);
    }
  }

  // Sanitize message (optional)
  if (data.message) {
    sanitized.message = sanitizeText(data.message, 2000);
  }

  return {
    isValid: errors.length === 0,
    errors,
    data: sanitized
  };
};

/**
 * Validates and sanitizes form data for appointments
 */
export const validateAppointmentForm = (data: {
  client_name: string;
  client_email: string;
  client_phone?: string;
  notes?: string;
}) => {
  return validateInquiryForm({
    client_name: data.client_name,
    client_email: data.client_email,
    client_phone: data.client_phone,
    message: data.notes
  });
};

/**
 * Rate limiting helper - tracks requests per IP/user
 */
export class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  
  constructor(
    private maxRequests: number = 10,
    private windowMs: number = 60000 // 1 minute
  ) {}

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const requestTimes = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = requestTimes.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
}

// Export a default rate limiter for forms
export const defaultRateLimiter = new RateLimiter(5, 60000); // 5 requests per minute

/**
 * Sanitizes search query input for database queries
 * PostgREST client escapes parameters, but this provides defense-in-depth
 * by limiting characters and length to prevent edge cases
 */
export const sanitizeSearchQuery = (query: string, maxLength: number = 200): string => {
  if (!query || typeof query !== 'string') return '';
  
  // Trim and limit length first
  const trimmed = query.trim().slice(0, maxLength);
  
  // Allow only alphanumeric, spaces, common punctuation, and international characters
  // Remove characters that could be problematic in filter syntax
  const sanitized = trimmed.replace(/[%_\\'"`;(){}[\]|<>]/g, '');
  
  return sanitized;
};

/**
 * Validates that a search query is safe and within limits
 */
export const isValidSearchQuery = (query: string): boolean => {
  if (!query || typeof query !== 'string') return false;
  const trimmed = query.trim();
  return trimmed.length > 0 && trimmed.length <= 200;
};